import React from 'react';

// lazy load template so that we have access to the window object
import IosTemplate from 'src/templates/ios';

//components
import { Link } from 'src/components/Inputs';

// meta data
const metaData = {
  layout: 'ios',
  'meta-title': 'Terms and Privacy',
  'meta-description':
    'As with everything we do, we’ve tried to simplify an overly complex process and make our Terms of Use and Privacy Policy as reader-friendly as possible',
};

const LegalIos = () => {
  return (
    <IosTemplate {...metaData}>
      <main className='page page--legal'>
        <h1 className='ios-legal-table-of-contents__heading'>
          End User Privacy Policy
        </h1>
        <ul className='ios-legal-table-of-contents'>
          <li className='ios-legal-table-of-contents__item'>
            <a
              href='#first-some-background'
              className='ios-legal-table-of-contents__section-link'
            >
              Background
            </a>
          </li>

          <li className='ios-legal-table-of-contents__item'>
            <a
              href='#data-we-collect-and-categories-of-sources'
              className='ios-legal-table-of-contents__section-link'
            >
              Data We Collect and Categories of Sources
            </a>
          </li>

          <li className='ios-legal-table-of-contents__item'>
            <a
              href='#how-we-use-your-data'
              className='ios-legal-table-of-contents__section-link'
            >
              How We Use Your Data
            </a>
          </li>

          <li className='ios-legal-table-of-contents__item'>
            <a
              href='#our-lawful-bases-for-processing-eea-and-uk-end-users-only'
              className='ios-legal-table-of-contents__section-link'
            >
              Our Lawful Bases for Processing (EEA and UK End Users)
            </a>
          </li>

          <li className='ios-legal-table-of-contents__item'>
            <a
              href='#how-we-share-your-data'
              className='ios-legal-table-of-contents__section-link'
            >
              How We Share Your Data
            </a>
          </li>
          <li className='ios-legal-table-of-contents__item'>
            <Link href='#our-retention-and-deletion-practices'>
              <a className='ios-legal-table-of-contents__section-link'>
                Our Retention and Deletion Practices
              </a>
            </Link>
          </li>
          <li className='ios-legal-table-of-contents__item'>
            <Link href='#protection-of-data'>
              <a className='ios-legal-table-of-contents__section-link'>
                Protection of Data
              </a>
            </Link>
          </li>

          <li className='ios-legal-table-of-contents__item'>
            <a
              href='#some-final-details'
              className='ios-legal-table-of-contents__section-link'
            >
              Some Final Details…
            </a>
          </li>
        </ul>
        <h1 id='end-user-privacy-policy' aria-level='1'>
          End User Privacy Policy
        </h1>
        <p className='h3' id='effective-date-february--'>
          Effective Date: December 8, 2025
        </p>
        <p>
          Our mission at Plaid is to unlock financial freedom for everyone.
          Privacy and security are very important to us.
        </p>
        <p>
          This End User Privacy Policy (“Policy”) is meant to help you (the “end
          user”) understand how we at Plaid collect, use, and share your data
          when you use Plaid products or services - for example, when you use
          Plaid Portal, or when you use Plaid to connect and share your data to
          power the applications (“apps”) you use. These apps, which offer many
          consumer services and products such as helping you save for
          retirement, manage your spending, streamline credit applications, or
          transfer money, are built and provided by our business customers
          (we’ll call them “developers” here), and powered by Plaid.
        </p>
        <p>
          This Policy applies to Plaid Inc., Plaid Financial Ltd., and Plaid,
          B.V. (collectively, “Plaid,” “we,” “our,” and “us”). Please see the
          <Link href='#contacting-plaid'>
            <a>“Contacting Plaid”</a>
          </Link>{' '}
          section below for which of these entities is responsible for
          processing your data.
        </p>
        <p>Other Policies:</p>

        <ul>
          <li>
            For information about services offered by Plaid Consumer Reporting
            Agency, Inc. (“Plaid CRA”), and how it processes data, please see
            the{' '}
            <Link href='/plaid-check-consumer-report/privacy-policy/'>
              <a>CRA Privacy Policy</a>
            </Link>
            .
          </li>
          <li>
            For information about how Plaid processes data we collect through
            our websites or when you interact with Plaid outside of using our
            products or services, such as when you email Plaid directly, please
            see our{' '}
            <Link href='https://plaid.com/legal/#all-audiences'>
              <a>All Audience Privacy Statement</a>
            </Link>
            and{' '}
            <Link href='https://plaid.com/legal/#cookie-policy'>
              <a>Cookie Policy</a>
            </Link>
            .
          </li>
          <li>
            For information about how Plaid treats photos and/or video images of
            your face that you may provide when using our services, including
            scans of facial geometry extracted from those images, which may be
            considered biometric data in some jurisdictions, please see{' '}
            <Link href='https://plaid.com/legal/#biometric-policy-and-release'>
              <a>Plaid's Biometric Policy and Release</a>
            </Link>
            .
          </li>
        </ul>
        <p>
          In addition, this Policy does not cover what developers of the apps
          you use do with your data. You should review the privacy policies or
          terms of service for those apps for information about their practices.
        </p>
        <h2
          data-section-title='Background'
          id='first-some-background'
          aria-level='3'
        >
          First, Some Background
        </h2>
        <h3 id='a-quick-note-about-plaid' aria-level='4'>
          A quick note about Plaid
        </h3>
        <p>
          Our mission at Plaid is to unlock financial freedom for everyone.
          Plaid helps consumers like you connect your financial accounts to
          Plaid or to third-party developers in order to provide you with
          products and services that you request.
        </p>
        <h3 id='about-this-policy' aria-level='4'>
          About this Policy
        </h3>
        <p>
          Our goal with this Policy is to provide a simple and straightforward
          explanation of what data Plaid collects from and about you and how we
          use and share that information. We value transparency and want to
          provide you with a clear and concise description of how we treat your
          data.
        </p>
        <p>
          This Policy does not cover what developers of the apps you use do with
          your data. You should review the privacy policies or terms of service
          for those apps for information about their practices. This Policy also
          does not cover data we collect through our websites or when you
          interact with Plaid outside of using our product or services, such as
          emailing Plaid directly. Please see our{' '}
          <a href='https://plaid.com/legal/#all-audiences'>
            All Audience Privacy Statement
          </a>{' '}
          and <a href='https://plaid.com/legal/#cookie-policy'>Cookie Policy</a>{' '}
          {''} for more information.
        </p>
        <p>
          Plaid’s services are not directed to individuals under 18 and we do
          not knowingly collect data relating to children.
        </p>
        <h2 id='our-data-practices'>Our Data Practices</h2>
        <p>
          Plaid is committed to providing end users with meaningful control over
          their data. This section describes Plaid’s data practices relating to
          our processing of data about you. We also provide summaries of our
          practices organized by category of data collected and by product at
          the end of this Policy in the{' '}
          <Link href='#summaries-of-processing-activities'>
            <a>Summaries of Processing Activities</a>
          </Link>{' '}
          section.
        </p>

        <h2
          data-section-title='Data we collect'
          id='data-we-collect-and-categories-of-sources'
          aria-level='3'
        >
          Data We Collect and Categories of Sources
        </h2>
        <p>
          As described in more detail below, the data we collect, use, and share
          depends on the Plaid products and services that you, and/or the app
          you have connected to, use. Depending on which of Plaid’s products or
          services you or your app use, Plaid may collect the following:
        </p>
        <ul>
          <li>
            <p>Data you provide to us;</p>
          </li>
          <li>
            <p>
              Data from financial institutions when you connect your financial
              account;
            </p>
          </li>
          <li>
            <p>
              Data from the electronic device you use to connect your financial
              account using Plaid or to otherwise interact with Plaid;
            </p>
          </li>
          <li>
            <p>Data from the developer of the app you have connected to; </p>
          </li>
          <li>
            <p>
              Data from our affiliates to provide support to you or to provide
              you with a better user experience; and/or
            </p>
          </li>
          <li>
            <p>
              Data from other sources, including service providers and identity
              verification and fraud prevention services.
            </p>
          </li>
        </ul>
        <p>
          <strong>Data you provide to us.</strong> When you use Plaid’s products
          or services (like when you use Plaid to connect your financial
          accounts to an app), we collect the following data from you as needed
          to power your particular app or Plaid product or service:
        </p>
        <ul>
          <li>
            <p>
              <strong>identifiers</strong> like name, email address, date of
              birth, Social Security number, and phone number;
            </p>
          </li>
          <li>
            <p>
              <strong>login data</strong> when required by the provider of your
              account, like your username and password, account and routing
              number, or a security token;
            </p>
          </li>
          <li>
            <p>
              <strong>
                any additional information needed to connect your accounts,
              </strong>{' '}
              including security questions and answers, and one-time password
              (OTP);
            </p>
          </li>
          <li>
            <p>
              information from <strong>documents or statements</strong> that you
              provide to Plaid, like bank statements or pay stubs; and/or
            </p>
          </li>
          <li>
            <p>
              information related to <strong>your use of our services</strong>,
              including which of our services you use, the dates and times of
              your use, and which financial institutions and apps you connect
              using Plaid.
            </p>
          </li>
        </ul>
        <p>
          When you provide login data and additional information needed to
          connect your accounts, you also give Plaid permission and authority to
          act on your behalf to access and transmit data to and from your
          financial institution.
        </p>
        <p>
          <strong>
            Data we collect from financial institutions about and from your
            accounts.
          </strong>{' '}
          Depending on which Plaid products or services you, or the developer of
          your app use, as well as what, and how information is made available,
          we may collect the following data from your financial institutions:
        </p>
        <ul>
          <li>
            <p>
              <strong>Account data</strong>, including financial institution
              name, account name, account type, account ownership, branch
              number, IBAN, BIC, account number, routing number, and sort code;
            </p>
          </li>
          <li>
            <p>
              <strong>Data about an account balance</strong>, including current
              and available balance;
            </p>
          </li>
          <li>
            <p>
              <strong>Data about credit accounts</strong>, including due dates,
              balances owed, payment amounts and dates, transaction history,
              credit limit, repayment status, and interest rate;
            </p>
          </li>
          <li>
            <p>
              <strong>Data about loan accounts</strong>, including due dates,
              repayment status, balances, payment amounts and dates, interest
              rate, guarantor, loan type, payment plan, and terms;
            </p>
          </li>
          <li>
            <p>
              <strong>Data about investment accounts</strong>, including
              transaction information, type of asset, identifying details about
              the asset, quantity, price, fees, and cost basis;
            </p>
          </li>
          <li>
            <p>
              <strong>Identifiers</strong> and data about the account owner(s),
              including name, email address, phone number, date of birth, and
              address information; interest rate;
            </p>
          </li>
          <li>
            <p>
              <strong>Data about account transactions</strong>, including
              amount, date, payee, type, quantity, price, location, involved
              securities, and a description of the transaction; and/or
            </p>
          </li>
          <li>
            <p>
              <strong>Data from your payroll and tax documents</strong>,
              including data about your income and employer, in cases where
              you’ve connected your payroll accounts or provided us with your
              pay stub or tax form information.
            </p>
          </li>
        </ul>
        <p>
          Depending on the Plaid service you or the developer of your app use,
          and the manner in which the data is made available, the data collected
          from your financial accounts may include data from all accounts (e.g.,
          checking, savings, credit card, and joint accounts) accessible through
          a single set of account credentials. For more specifics about data
          collected in connection with different products and services, see{' '}
          <a href='#sopa-by-product'>
            Summary of Processing Activities by Product.
          </a>
        </p>

        <p>
          <strong>Data we receive from your devices.</strong> When you use a
          device, like your smartphone, tablet, or computer, to interact with
          our services (including through a developer’s app), we may collect the
          following data about that device:
        </p>
        <ul>
          <li>
            <p>internet protocol (IP) address;</p>
          </li>
          <li>
            <p>timezone setting and location, device location;</p>
          </li>
          <li>
            <p>hardware model and operating system;</p>
          </li>
          <li>
            <p>features within our services you access;</p>
          </li>
          <li>
            <p>browser data;</p>
          </li>
          <li>
            <p>network data; and</p>
          </li>
          <li>
            <p>
              other technical data about the device (such as settings and
              preferences).
            </p>
          </li>
        </ul>
        <p>
          <strong>
            Data we receive about you from the developers of apps powered by
            Plaid.
          </strong>{' '}
          When needed for Plaid to provide a service (like verifying your
          identity, protecting against fraud or risk, or enabling a bank
          transfer), the developers of the apps you use may provide us with
          identifiers and commercial information about you, like your name,
          Social Security number, email address, phone number, or information
          about your financial accounts and transactions.
        </p>
        <p>
          <strong>
            Data from our affiliates to provide support to you or to provide you
            with a better user experience.{' '}
          </strong>
          We may collect data from our affiliates (these are companies related
          to us by common ownership or control) to provide support to you or
          improve your experience. For example, if you elect for Plaid CRA to
          remember financial accounts associated with your phone number, we may
          collect the following data from Plaid CRA in order to help you connect
          to your accounts faster when using our services:
        </p>
        <ul>
          <li>
            <p>
              <strong>identifiers</strong> like name, email address, and phone
              number;
            </p>
          </li>
          <li>
            <p>
              <strong>login data </strong> when required by the provider of your
              account, like your username and password, bank name, account and
              routing number, or a security token;
            </p>
          </li>
          <li>
            <p>
              when needed,
              <strong>
                {' '}
                data to help verify your identity and connect your accounts,{' '}
              </strong>
              including your date of birth, security questions and answers, and
              one-time password (OTP); and/or
            </p>
          </li>
          <li>
            <p>data from your device (as explained in more detail above).</p>
          </li>
        </ul>
        <p>
          <strong>Data we receive about you from other sources.</strong> When
          needed to provide a service, identify your device, or to help prevent
          fraud, abuse, or security threats, we may also receive data about you
          directly from third parties, including your wireless carrier, agents
          and our service providers.
        </p>
        <p>
          <strong>Information we derive from the data we collect.</strong> We
          may derive additional information about you from the data we collect.
          For example, we may infer your geolocation, your annual income, or the
          type of account or subaccount you’ve chosen to connect―such as when
          you connect your loan accounts, so we can let the developer know
          whether the account is for a mortgage, student loan, or credit card.
        </p>
        <h2
          data-section-title='How we use your data'
          id='how-we-use-your-data'
          aria-level='3'
        >
          How We Use Your Data
        </h2>
        <p>
          We use your data for the following business and commercial purposes:
        </p>
        <ul>
          <li>
            <p>
              <strong>Provide Services:</strong> To operate, provide, service,
              process, and maintain our products and services.
            </p>
          </li>
          <li>
            <p>
              <strong>Develop Existing Services:</strong> To improve, enhance,
              modify, add to, and further develop our services.
            </p>
          </li>
          <li>
            <p>
              <strong>
                Help Prevent Fraud, Verify Your Identity, or Protect Privacy:
              </strong>{' '}
              To verify your identity and help protect you, developers, our
              partners, Plaid, and others from and prevent actual or potential
              fraud, unauthorized transactions, claims, other liabilities,
              malicious activity, and other privacy and security-related
              concerns.
            </p>
          </li>
          <li>
            <p>
              <strong>Develop New Services:</strong> To develop new products and
              services.
            </p>
          </li>
          <li>
            <p>
              <strong>Develop Insights:</strong> To develop insights based on
              the data we’ve collected about you. This includes your transaction
              data, other financial data, data about which financial accounts
              you have connected to which apps, and data from other sources, to
              help us, your financial institutions, and the developers of your
              connected apps provide services and/or a better user experience to
              you, like providing you with a faster connection and onboarding
              experience, faster access to your funds, to help detect and
              prevent potentially fraudulent activity, or personalize their
              services to you.
            </p>
          </li>
          <li>
            <p>
              <strong>Provide Support:</strong> To provide support to you or to
              developers, including to help respond to your inquiries related to
              our services or developers’ apps.
            </p>
          </li>
          <li>
            <p>
              <strong>Communicate With You: </strong>To communicate with you and
              send you things like technical notices, updates, security alerts,
              and messages.
            </p>
          </li>
          <li>
            <p>
              <strong>Investigate Misuse and Misconduct:</strong> To investigate
              any misuse of our service or developers’ apps, including
              violations of our{' '}
              <Link href='https://plaid.com/legal/#developer-policy'>
                <a>Developer Policy</a>
              </Link>
              , criminal activity, or other unauthorized access to our services.
            </p>
          </li>
          <li>
            <p>
              <strong>For Legal Purposes:</strong> To comply with contractual
              and legal obligations under applicable law and for other legal
              purposes such as to establish and defend against claims.
            </p>
          </li>
          <li>
            <p>
              <strong>With Your Consent:</strong> For other notified purposes
              with your consent or at your direction.
            </p>
          </li>
        </ul>
        <p>
          We collect, use, and share data that has been aggregated or anonymized
          in a manner that does not identify you personally for any purpose
          permitted under applicable law. This includes creating or using
          aggregated or anonymized data to develop new services, or to
          facilitate research, and for analytics purposes to help assess the
          speed, accuracy, and/or security of our services.{' '}
        </p>
        <h2
          data-section-title='Our Lawful Bases for Processing (EEA and UK End Users Only)'
          id='our-lawful-bases-for-processing-eea-and-uk-end-users-only'
          aria-level='3'
        >
          Our Lawful Bases for Processing (EEA and UK End Users)
        </h2>
        <p>
          For individuals in the European Economic Area (“EEA”) or the United
          Kingdom (“UK”), Plaid only processes your personal data when we have a
          valid legal basis to do so. Our legal basis for processing the data we
          collect will depend on what information we collected and the context
          for processing it. Generally, we will only collect and process your
          data where:
        </p>
        <ul>
          <li>
            <p>
              we are bound by any contract or agreement with you (for example,
              to comply with our end user services agreements) and must adhere
              to various legal requirements when offering regulated services,
              including our account information and payment services.
            </p>
          </li>
          <li>
            <p>
              we require your data to comply with our legal obligations under
              applicable law, to safeguard Plaid's legal rights, and to prevent
              and identify criminal activities such as money laundering and
              fraud. For these purposes, Plaid may find it necessary to share
              your personal data with entities such as courts, law enforcement
              agencies, and providers of anti-money laundering services;
            </p>
          </li>
          <li>
            <p>
              processing is necessary for our legitimate interests to
              effectively maintain the integrity of our services. This includes
              retrieving account information or providing payment initiation
              services (as well as verifying the output of this activity),
              engaging in communication with you, and ensuring that Plaid, as a
              regulated entity, upholds the expected standards (including those
              stipulated by the Financial Conduct Authority for Plaid Financial
              Ltd and De Nederlandsche Bank for Plaid B.V).; or
            </p>
          </li>
          <li>
            <p>you have given your consent to do so.</p>
          </li>
        </ul>
        <p>
          To the extent we rely on consent to collect and process your data, you
          have the right to withdraw your consent at any time per the
          instructions provided in this Policy.
        </p>
        <h2
          data-section-title='How We Share Your Data'
          id='how-we-share-your-data'
        >
          How We Share Your Data
        </h2>
        <p>We share your data for the following reasons:</p>
        <ul>
          <li>
            <p>
              With the developer of the app you are using and as directed by
              that developer;
            </p>
          </li>
          <li>
            <p>To enforce any contract with you;</p>
          </li>
          <li>
            <p>
              With our data processors and other service providers, partners,
              agents or contractors in connection with the services they perform
              for us or developers;
            </p>
          </li>
          <li>
            <p>
              With financial institutions to help establish, maintain, or manage
              a connection you’ve chosen to make between your financial
              institution accounts and the products or services you use, as well
              as to help them protect your financial accounts and offer you
              personalized services such as returning user experiences;
            </p>
          </li>
          <li>
            <p>
              If we believe in good faith that disclosure is appropriate to
              comply with applicable law, regulation, or legal process (like a
              court order or subpoena);
            </p>
          </li>
          <li>
            <p>
              In connection with a change in ownership or control of all or a
              part of our business (like a merger, acquisition, reorganization,
              or bankruptcy);
            </p>
          </li>
          <li>
            <p>
              Between and among Plaid and our current and future parents,
              affiliates, subsidiaries and other companies under common control
              or ownership ;
            </p>
          </li>
          <li>
            <p>
              As we believe reasonably appropriate to protect against and
              prevent actual or potential fraud, unauthorized transactions,
              claims, other liabilities, or otherwise protect the rights,
              privacy, safety, or property of you, developers, our partners,
              Plaid, and others; or
            </p>
          </li>
          <li>
            <p>
              For any other notified purpose with your consent or at your
              direction.
            </p>
          </li>
        </ul>
        <p>
          (For US users) We do not share your data with non-affiliated third
          parties except as permitted by law (as authorized by 12 C.F.R. §
          1016.14 and 1016.15).
        </p>
        <p>
          {' '}
          When you link your financial accounts through Plaid, we typically use
          Google’s reCAPTCHA service to help detect fraud and abuse. Google
          reCAPTCHA is integrated into Plaid products and services in order to
          ensure that entries made in online forms, for example, are actually
          made by real persons and are not automated by software (or bots).
        </p>
        <p>
          Google reCAPTCHA analyzes the behavior of users of our products and
          services using different characteristics. Google reCAPTCHA processes
          personal data such as your IP address, your length of stay on our
          website and further information about your use of Plaid products and
          services. This data will be transmitted and stored by Google on
          servers in the United States and other countries. Google has certified
          with the EU-US Data Privacy Framework; the certificate is available{' '}
          <Link href='https://www.dataprivacyframework.gov/s/participant-search/participant-detail?id=a2zt000000001L5AAI&status=Active'>
            <a>here</a>
          </Link>
          . When reCAPTCHA is used,{' '}
          <Link href='https://policies.google.com/privacy?hl=en'>
            Google’s Privacy Policy
          </Link>{' '}
          and{' '}
          <Link href='https://policies.google.com/terms?hl=en'>
            Terms of Use
          </Link>{' '}
          apply to reCAPTCHA and information Google collects through reCAPTCHA.
        </p>
        <p>
          We only share your personal financial data with third parties to power
          the services you requested, when you consent, and/or to protect
          against actual or potential fraud, unauthorized transactions, claims,
          other liabilities, malicious activity, and other privacy and
          security-related concerns.
        </p>
        <p>
          We may collect and share cookie data with third parties when you visit
          our website, or we may allow third parties to collect this cookie data
          from our sites. Please see our{' '}
          <a href='https://plaid.com/legal/#cookie-policy'>Cookie Policy</a> and{' '}
          <a href='https://plaid.com/legal/#all-audiences'>
            All Audience Privacy Policy
          </a>{' '}
          for more details.
        </p>
        <h2
          data-section-title='Our Retention and Deletion Practices'
          id='our-retention-and-deletion-practices'
          aria-level='3'
        >
          Our Retention and Deletion Practices
        </h2>
        <p>
          We retain your data only as long as it is needed. To determine whether
          the data is needed, we consider the reason your data was collected and
          used and any legal requirements to hold onto your data. We review your
          data periodically to ensure it is still needed to fulfill the purpose
          for which it was collected or any other legal requirements. If a
          developer removes your connection from their app to your data, Plaid’s
          systems are designed to automatically delete your personal data,
          subject to certain exceptions where we may still retain your
          information.
        </p>
        <p>
          The exceptions to this may be if: (a) you've established a connection
          with another developer's app through Plaid that is still active; (b)
          Plaid needs your data to continue providing you with a Plaid product
          or service you requested; (c) Plaid is required by law to keep your
          data; (d) Plaid needs your data to help protect against or prevent
          fraud or protect privacy, provide support, or investigate misuse and
          misconduct; (e) Plaid has aggregated, de-identified, or anonymized
          your data such that it cannot be reasonably reidentified; or (f) we
          request - and you specifically agree - to allow us to retain your data
          longer.
        </p>
        <p>
          Your data will only be processed as required by law or in accordance
          with this Policy.
        </p>
        <p>
          Please refer to the{' '}
          <Link href='#your-data-protection-rights'>
            <a>“Your Data Protection Rights”</a>
          </Link>{' '}
          section of this Policy for options that may be available to you,
          including how to request deletion of your data. The{' '}
          <Link href='#your-additional-privacy-controls'>
            <a>“Your Additional Privacy Controls”</a>
          </Link>{' '}
          section of this Policy also provides details about tools available to
          you to help you view and manage the connections you’ve made using
          Plaid. You can also contact us about our data retention practices
          using the contact information in the{' '}
          <Link href='#contacting-plaid'>
            <a>“Contacting Plaid”</a>
          </Link>{' '}
          section below.
        </p>
        <h2 id='protection-of-data' aria-level='3'>
          Protection of Data
        </h2>
        <p>
          Plaid’s security policies and practices are designed to protect the
          confidentiality and integrity of your data. Plaid implements controls
          designed to limit access to this data to personnel who have a business
          reason to know it and prohibits its personnel from unlawfully
          accessing, using or disclosing this data.
        </p>
        <h2 data-section-title='Some Final Details…' id='some-final-details'>
          Some Final Details…
        </h2>
        <h2 id='international-data-transfers' aria-level='3'>
          International Data Transfers
        </h2>
        <p>
          We operate internationally, and so we transfer the data we collect
          about you across international borders for processing and storage (for
          example, we transfer data from the EEA and UK to the United States and
          store data in AWS regions located in the United States). When we
          transfer data to a different country or territory, we follow
          applicable data protection laws in doing so. In particular, when we
          transfer data from the EEA or UK across other international borders,
          we rely on adequacy decisions, data transfer agreements, or other EU
          Commission- or UK Secretary of State-approved (as applicable)
          mechanisms for such transfers, including standard contractual clauses.
          You can ask for a copy of these standard contractual clauses by
          contacting us as set out below. Prior to transferring data from the
          EEA or UK, we carry out transfer impact assessments and implement any
          supplementary measures to ensure any data transferred will be
          maintained in accordance with EEA and UK requirements.
        </p>
        <h2 id='your-data-protection-rights' aria-level='3'>
          Your Data Protection Rights
        </h2>
        <p>
          Regardless of where you live, we will honor the following rights
          related to your personal data, subject to some limitations and
          exceptions provided by law, and you will not be discriminated against
          for exercising them:
        </p>
        <ul>
          <li>
            <p>Access data collected about you;</p>
          </li>
          <li>
            <p>
              Request access to more details about the categories and specific
              pieces of personal information we may have collected about you in
              the last 12 months (including personal information disclosed for
              business purposes);
            </p>
          </li>
          <li>
            <p>
              Request, under certain circumstances, that we rectify or update
              your data that is inaccurate or incomplete;
            </p>
          </li>
          <li>
            <p>
              Request, under certain circumstances, that we erase or restrict
              the processing of your data;
            </p>
          </li>
          <li>
            <p>
              Object to our processing of your data under certain conditions
              provided by law;
            </p>
          </li>

          <li>
            <p>
              Where processing of your data is based on consent, withdraw that
              consent;
            </p>
          </li>
          <li>
            <p>
              Request that we provide data collected about you in a structured,
              commonly used and machine-readable format so that you can transfer
              it to another company, where technically feasible.
            </p>
            <ul>
              <li>
                <p>
                  Please note that for an official record of your financial
                  information you should make that request directly to your
                  financial institution.{' '}
                </p>
              </li>
            </ul>
          </li>
        </ul>
        <p>
          Depending on where you live, you may have the right to lodge a
          complaint. We welcome and appreciate the chance to address any
          concerns you may have and encourage you to contact us. In addition,
          and depending on your jurisdiction, you may have the right to make a
          complaint at any time to your (data protection) supervisory authority.
          For end users in the EEA, you can find contact information for the
          European Data Protection Board (EDPB) on the EDPB’s website{' '}
          <Link href='https://edpb.europa.eu/about-edpb/about-edpb/members_en'>
            <a>here</a>
          </Link>
          . For end users in the UK, you can find contact information for the
          Information Commissioner’s Office (ICO) on the ICO’s website{' '}
          <Link href='https://ico.org.uk/'>
            <a>here</a>
          </Link>
          . For end users in Switzerland this is the Federal Data Protection and
          Information Commissioner which you can find{' '}
          <Link href='https://www.edoeb.admin.ch/edoeb/en/home.html'>
            <a>here</a>
          </Link>
          .
        </p>
        <p>
          To exercise any rights you have, you can submit a request using our{' '}
          <a href='https://my.plaid.com/data-subject-request-form'>
            online form
          </a>
          . You can also contact us as described in the “Contacting Plaid”
          section below to exercise any of your data protection rights. You may
          be required to provide additional information necessary to confirm
          your identity before we can respond to your request.
        </p>
        <p>
          If we receive your request from an authorized agent, we may ask for
          evidence that the agent has valid written authority, like a power of
          attorney, to submit requests on your behalf.
        </p>
        <p>
          We will consider requests and provide our response within a reasonable
          period of time (and within any time period required by applicable
          law). Please note, however, that certain data may be exempt from such
          requests, for example if we need to keep the data to comply with our
          own legal obligations or to establish, exercise, or defend legal
          claims.
        </p>

        <h2 id='your-additional-privacy-controls' aria-level='3'>
          Your Additional Privacy Controls
        </h2>
        <p>
          Plaid developed the <a href='http://my.plaid.com'>Plaid Portal</a> to
          provide you with a convenient, centralized way to view and manage the
          connections you’ve made using Plaid.
        </p>
        <p>
          If you’re located in the U.S., you can sign up for Plaid Portal by
          visiting my.plaid.com, verifying your phone number and email address,
          and creating a password. Once you’ve signed up for Plaid Portal,
          you’ll be directed to a dashboard that can show you financial accounts
          you’ve connected to your chosen apps using Plaid, and the types of
          data shared with those apps. Additionally, the Plaid Portal provides
          you with controls to terminate the connection between apps and your
          financial accounts and delete associated data stored in Plaid’s
          systems.
        </p>

        <h2 id='changes-to-this-policy' aria-level='3'>
          Changes To This Policy
        </h2>
        <p>
          We update or change this Policy from time to time. If we make any
          updates or changes, we will post the new policy on Plaid's website at{' '}
          <Link href='https://plaid.com/legal'>
            <a>https://plaid.com/legal</a>
          </Link>{' '}
          and update the effective date at the top of this Policy. We will also
          notify developers of any material changes in accordance with our
          developer agreements, as they may be better positioned to notify you
          about changes to this Policy. If we have an email address for you, we
          will also send you an email notifying you of the update.
        </p>
        <h2 id='contacting-plaid' aria-level='3'>
          Contacting Plaid
        </h2>
        <p>
          If you have any questions or complaints about this Policy, or about
          our privacy practices generally, you can contact us at{' '}
          <a href='mailto:privacy@plaid.com'>privacy@plaid.com</a> or by mail
          at:
        </p>
        <p>
          <em>If you reside outside the EEA or UK:</em>
        </p>
        <div className='ios-legal-address'>
          Plaid Inc.
          <br />
          Attn: Legal
          <br />
          PO Box 7775 #35278
          <br />
          San Francisco, California 94120-7775
          <br />
          U.S.A.
        </div>
        <p>
          For both Plaid Financial Ltd. and Plaid, B.V. we have appointed a data
          protection officer (DPO) who is also responsible for overseeing
          questions in relation to this Policy and can be contacted via email at{' '}
          <Link href='mailto:privacy@plaid.com'>
            <a>privacy@plaid.com</a>
          </Link>{' '}
          .
        </p>
        <p>
          <br />
          <em>If you reside in the UK:</em>
        </p>
        <div className='ios-legal-address'>
          Plaid Financial Ltd.
          <br />
          Attn: Legal
          <br />
          New Penderel House, 4th Floor
          <br />
          283-288 High Holborn
          <br />
          London, United Kingdom, WC1V 7HP
        </div>
        <p>
          <br />
          <em>If you reside in the EEA:</em>
        </p>
        <div className='ios-legal-address'>
          Plaid, B.V.
          <br />
          Attn: Legal
          <br />
          Muiderstraat 1
          <br />
          1011PZ Amsterdam
          <br />
          The Netherlands
        </div>

        <h2 id='summaries-of-processing-activities' aria-level='3'>
          Summaries of Processing Activities
        </h2>
        <h2 id='sopa-by-category-of-information' aria-level='3'>
          Summary of Processing Activities by Category of Information
        </h2>
        <p>
          To help provide even greater transparency around our practices, we
          consolidated the information provided in our Policy above into a table
          that matches the categories of data Plaid collects about you with the
          sources of the data, uses of the data, and the categories of
          recipients with whom Plaid shares the data. For more detailed
          descriptions of our data collection, use, and sharing practices,
          please refer back to the sections of the Policy above.
        </p>
        <br />
        <br />
        <div className='eupp-container'>
          <table className='eupp-table'>
            <thead>
              <tr>
                <th>
                  <span>Category of Personal Data &amp; Examples</span>
                </th>
                <th>
                  <span>Source of Personal Data</span>
                </th>
                <th>
                  <span>Uses of Personal Data</span>
                </th>
                <th>
                  <span>
                    Categories of Recipients with Whom Personal Data may be
                    Shared
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th
                  scope='row'
                  data-label='Category of Personal Data &amp; Examples:'
                >
                  <span>Identifiers</span>
                  <br />
                  <br />
                  <span>
                    for example: name, email address, phone number, and username
                  </span>
                </th>
                <td data-label='Source of Personal Data:'>
                  <ul>
                    <li>
                      <span>Data you provide</span>
                    </li>
                    <li>
                      <span>Data we collect from your financial accounts</span>
                    </li>
                    <li>
                      <span>Data we receive from your devices</span>
                    </li>
                    <li>
                      <span>
                        Data we receive about you from the developers of apps
                        powered by Plaid
                      </span>
                    </li>
                    <li>
                      <span>
                        Data from our affiliates to provide you with services or
                        support
                      </span>
                    </li>
                    <li>
                      <span>Data we receive about you from other sources</span>
                    </li>
                  </ul>
                </td>
                <td data-label='Uses of Personal Data:'>
                  <ul>
                    <li>
                      <span>Provide Services</span>
                    </li>
                    <li>
                      <span>Develop Existing Services</span>
                    </li>
                    <li>
                      <span>
                        Help Prevent Fraud or Protect Privacy and Security
                      </span>
                    </li>
                    <li>
                      <span>Develop New Services</span>
                    </li>
                    <li>
                      <span>Develop Insights</span>
                    </li>
                    <li>
                      <span>Provide Support</span>
                    </li>
                    <li>
                      <span>Investigate Misuse and Violations</span>
                    </li>
                    <li>
                      <span>For Legal Purposes</span>
                    </li>
                    <li>
                      <span>With Your Consent</span>
                    </li>
                  </ul>
                </td>
                <td data-label='Categories of Recipients with Whom Personal Data may be Shared:'>
                  <ul>
                    <li>
                      <span>
                        Developer(s) of app(s) you use, directed by the
                        developers
                      </span>
                    </li>
                    <li>
                      <span>Financial institutions</span>
                    </li>
                    <li>
                      <span>Providers of payment services</span>
                    </li>
                    <li>
                      <span>
                        Partners and contractors in connection with services
                        they perform
                      </span>
                    </li>
                    <li>
                      <span>Professional advisors</span>
                    </li>
                    <li>
                      <span>
                        Service providers who help analyze data for business
                        purposes
                      </span>
                    </li>
                    <li>
                      <span>Cloud storage services providers</span>
                    </li>
                    <li>
                      <span>Software development services providers</span>
                    </li>
                    <li>
                      <span>Fraud prevention services providers</span>
                    </li>
                    <li>
                      <span>Identity verification services providers</span>
                    </li>
                    <li>
                      <span>Plaid group entities</span>
                    </li>
                    <li>
                      <span>
                        Law enforcement and other governmental authorities
                      </span>
                    </li>
                  </ul>
                </td>
              </tr>
              <tr>
                <th
                  scope='row'
                  data-label='Category of Personal Data &amp; Examples:'
                >
                  <span>Financial Data</span>
                  <br />
                  <br />
                  <span>
                    for example: financial account name and number, balance, and
                    transaction history
                  </span>
                </th>
                <td data-label='Source of Personal Data:'>
                  <ul>
                    <li>
                      <span>Data you provide</span>
                    </li>
                    <li>
                      <span>
                        Data we receive about you from the developers of apps
                        powered by Plaid
                      </span>
                    </li>
                    <li>
                      <span>Data we collect from your financial accounts</span>
                    </li>
                    <li>
                      <span>Data we receive about you from other sources</span>
                    </li>
                    <li>
                      <span>
                        Data we derive from data we collect from you or other
                        sources
                      </span>
                    </li>
                  </ul>
                </td>
                <td data-label='Uses of Personal Data:'>
                  <ul>
                    <li>
                      <span>Provide Services</span>
                    </li>
                    <li>
                      <span>Develop Existing Services</span>
                    </li>
                    <li>
                      <span>
                        Help Prevent Fraud or Protect Privacy and Security
                      </span>
                    </li>
                    <li>
                      <span>Develop New Services</span>
                    </li>
                    <li>
                      <span>Develop Insights</span>
                    </li>
                    <li>
                      <span>Provide Support</span>
                    </li>
                    <li>
                      <span>Investigate Misuse and Violations</span>
                    </li>
                    <li>
                      <span>For Legal Purposes</span>
                    </li>
                    <li>
                      <span>With Your Consent</span>
                    </li>
                  </ul>
                </td>
                <td data-label='Categories of Recipients with Whom Personal Data may be Shared:'>
                  <ul>
                    <li>
                      <span>
                        Developer(s) of application(s) you use, and as directed
                        by the developer
                      </span>
                    </li>
                    <li>
                      <span>Financial institutions</span>
                    </li>
                    <li>
                      <span>Providers of payments services</span>
                    </li>
                    <li>
                      <span>
                        Partners and contractors in connection with the services
                        they perform for us or developers
                      </span>
                    </li>
                    <li>
                      <span>Professional advisors</span>
                    </li>
                    <li>
                      <span>
                        Service providers who help Plaid analyze data for
                        Plaid's business purposes
                      </span>
                    </li>
                    <li>
                      <span>Cloud storage services providers</span>
                    </li>
                    <li>
                      <span>Software development services providers</span>
                    </li>
                    <li>
                      <span>Fraud prevention services providers</span>
                    </li>
                    <li>
                      <span>Identity verification services providers</span>
                    </li>
                    <li>
                      <span>Plaid group entities</span>
                    </li>
                    <li>
                      <span>
                        Third parties when compelled by law, such as to law
                        enforcement or other governmental authorities
                      </span>
                    </li>
                  </ul>
                </td>
              </tr>
              <tr>
                <th
                  scope='row'
                  data-label='Category of Personal Data &amp; Examples:'
                >
                  <span>Commercial Data</span>
                  <br />
                  <br />
                  <span>
                    for example: data relating to which of our services you use
                    through developer apps and the dates and times of your use
                  </span>
                </th>
                <td data-label='Source of Personal Data:'>
                  <ul>
                    <li>
                      <span>Data we collect from your financial accounts</span>
                    </li>
                    <li>
                      <span>
                        Data we receive about you from the developers of apps
                        powered by Plaid
                      </span>
                    </li>
                    <li>
                      <span>
                        Data from our affiliates to provide you with services or
                        support
                      </span>
                    </li>
                    <li>
                      <span>Data we receive about you from other sources</span>
                    </li>
                    <li>
                      <span>
                        Data we derive from data we collect from you or other
                        sources
                      </span>
                    </li>
                  </ul>
                </td>
                <td data-label='Uses of Personal Data:'>
                  <ul>
                    <li>
                      <span>Provide Services</span>
                    </li>
                    <li>
                      <span>Develop Existing Services</span>
                    </li>
                    <li>
                      <span>
                        Help Prevent Fraud or Protect Privacy and Security
                      </span>
                    </li>
                    <li>
                      <span>Develop New Services</span>
                    </li>
                    <li>
                      <span>Develop Insights</span>
                    </li>
                    <li>
                      <span>Provide Support</span>
                    </li>
                    <li>
                      <span>Investigate Misuse and Violations</span>
                    </li>
                    <li>
                      <span>For Legal Purposes</span>
                    </li>
                    <li>
                      <span>With Your Consent</span>
                    </li>
                  </ul>
                </td>
                <td data-label='Categories of Recipients with Whom Personal Data may be Shared:'>
                  <ul>
                    <li>
                      <span>
                        Developer(s) of application(s) you use, and as directed
                        by the developer
                      </span>
                    </li>
                    <li>
                      <span>Financial institutions</span>
                    </li>
                    <li>
                      <span>Providers of payments services</span>
                    </li>
                    <li>
                      <span>
                        Partners and contractors in connection with the services
                        they perform for us or developers
                      </span>
                    </li>
                    <li>
                      <span>Professional advisors</span>
                    </li>
                    <li>
                      <span>
                        Service providers who help Plaid analyze data for
                        Plaid's business purposes
                      </span>
                    </li>
                    <li>
                      <span>Cloud storage services providers</span>
                    </li>
                    <li>
                      <span>Software development services providers</span>
                    </li>
                    <li>
                      <span>Fraud prevention services providers</span>
                    </li>
                    <li>
                      <span>Identity verification services providers</span>
                    </li>
                    <li>
                      <span>Plaid group entities</span>
                    </li>
                    <li>
                      <span>
                        Third parties when compelled by law, such as to law
                        enforcement or other governmental authorities
                      </span>
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id='sopa-by-product' aria-level='3'>
          Summary of Processing Activities by Product
        </h2>
        <p>
          As noted above, the data we collect and use depends on the Plaid
          products and services that you and the developer use. To make it
          easier to understand what data is collected and used for which product
          and service, we put together the below summaries of our data
          collection and use by product. Some of these products are only
          available to US based users, and others are only available to EEA and
          UK based users. If you are not sure which Plaid products or services
          your connected applications use, please contact the developer of the
          application. (Note that this list reflects Plaid’s product suite as of
          the effective date of this Policy and may not include products or
          services in development as of that date.)
        </p>
        <p>
          Additionally, data protection law in certain jurisdictions, such as
          the UK and EU, differentiates between the “controller” and “processor”
          of data. Depending on which product or service you and your developer
          are using, Plaid may be the controller or the processor of the data
          collected under the relevant data protection law. Unless otherwise
          specified in the product description below, Plaid is the controller of
          the personal data collected.{' '}
        </p>
        <h2>Assets</h2>
        <p>
          Assets enables you to provide access to a point-in-time view of your
          chosen financial account data—like account balances, transaction
          histories, and account holder identity information—to Plaid and the
          apps and services you choose. We collect identifiers, financial
          information, commercial data, location data, electronic network
          activity data, and we derive inferences from this data. We use this
          data as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Auth</h2>
        <p>
          Auth helps to enable you to authenticate your financial account data
          and provide access to your account and routing numbers to Plaid and
          the apps and services you choose. We collect identifiers, financial
          data, commercial data, location data, and electronic network activity
          data. We use this information as described in the{' '}
          <a href='#how-we-use-your-data'>How We Use Your Data</a> section of
          this Policy.
        </p>
        <h2>Balance</h2>
        <p>
          Balance enables you to provide access to your real-time financial
          account balances and/or inferences derived from your account balances
          and other account information to Plaid and the apps and services you
          choose so they can help you do things like avoid overdrawing your
          account before you make a money transfer. We collect identifiers,
          financial data, commercial data, location data, and electronic network
          activity data and in some cases we derive inferences from this data.
          We use this data as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Bank Intelligence</h2>
        <p>
          Bank Intelligence facilitates sharing insights based on the account
          connections you make via Plaid to the financial institutions at which
          you hold accounts so that they can provide you with better-designed
          products, offer personalized experiences, enhance long-term
          engagement, and protect against fraud. We collect identifiers,
          financial data, commercial data, and electronic network activity data,
          and we derive inferences from this data. We use this data as described
          in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Identity and Identity Match</h2>
        <p>
          Identity enables you to provide access to the account holder data held
          by your financial institution—such as your name, email address, phone
          number, and mailing address—to Plaid and the apps and services you
          choose to help them do things like verify your identity or prefill
          your account data within the app. Identity Match helps the apps and
          services you use verify your identity against account holder data held
          by your financial institution. We collect identifiers, financial data,
          commercial data, location data, and electronic network activity data.
          We use this data as described above in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>

        <h2>Identity Verification and Monitor</h2>
        <p>
          Identity Verification and Monitor (“IDV”) facilitate identity
          verification, fraud detection, and watchlist screening services for
          developers. When we provide Identity Verification and Monitor services
          to developers, we only collect and process your data as a service
          provider to that developer, and so the developers determine the
          purpose and means by which your data is processed. For more details on
          how we process end user data for developers and on the developer’s
          instruction to provide Identity Verification and Monitor services, see
          IDV's
          <Link href='https://cognitohq.com/privacy-statement'>
            <a> End User Privacy Statement.</a>
          </Link>{' '}
          When Plaid collects and processes your data as part of IDV’s Remember
          Me feature, which allows Plaid to remember you and your identifying
          data for the next time you verify with IDV, we use this data as
          described above in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Income</h2>
        <p>
          Income enables you to provide access to data pertaining to your income
          and employment to Plaid and the apps and services you choose. We
          collect identifiers, financial data, commercial data, location data,
          employment, income and other professional data, electronic network
          activity data, and we derive inferences from this data. We use this
          data as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Investments</h2>
        <p>
          Investments enables you to provide access to data from your
          retirement, brokerage, education savings, and health savings accounts
          to Plaid and the apps or services you choose so they can do things
          like provide you with personal financial and wealth management tools.
          We collect identifiers, financial data, commercial data, location
          data, professional data, electronic network activity data, and we
          derive inferences from this data. We use this data as described in the{' '}
          <a href='#how-we-use-your-data'>How We Use Your Data</a> section of
          this Policy.
        </p>
        <h2>Investments Move</h2>
        <p>
          Investments Move allows you to provide access to brokerage account
          data to Plaid and the apps or services you choose so they can automate
          brokerage account transfers, reduce operational complexity, and avoid
          rejections. We collect identifiers, financial data, commercial data,
          location data, professional data, electronic network activity data,
          and we derive inferences from this data. We use this data as described
          in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Liabilities</h2>
        <p>
          Liabilities enables you to provide access to financial data from your
          credit card, mortgage, and student loan accounts to Plaid and the apps
          and services you choose. We collect identifiers, financial data,
          commercial data, location data, electronic network activity data, and
          we derive inferences from this data. We use this data as described in
          the <a href='#how-we-use-your-data'>How We Use Your Data</a> section
          of this Policy.
        </p>
        <h2>Payment Initiation and Variable Recurring Payments</h2>
        <p>
          Payment Initiation enables users of EU and UK services to make one
          time, recurring, or variable recurring payments into accounts. This is
          for services like bank transfers, standing orders, and variable
          recurring payments and one-off or recurring payments out of accounts,
          like withdrawals and refunds. Variable Recurring Payments enables our
          EU and UK users to authenticate a bank account one time, and then make
          future payments without the need for additional authentication. We
          collect identifiers, financial data, commercial data, location data,
          electronic network activity data, and we derive inferences from this
          data. We use this data as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Payouts</h2>
        <p>
          Payouts enables users of EU and UK services to seamlessly and
          instantly make withdrawals and refunds from an app or service that you
          are using, by opening and pre-funding an e-money account from which
          funds can be sent to users that are requesting a payout. We collect
          identifiers, financial data, commercial data, location data,
          electronic network activity data, and we derive inferences from this
          data. We use this data as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Permissions Manager</h2>
        <p>
          Permissions Manager enables you to provide details of account
          connections you make via Plaid to the financial institution that holds
          that account so that they can provide you with privacy management
          tools and engage in risk management. We collect identifiers, financial
          data, commercial data, location data, professional data, electronic
          network activity data, and we derive inferences from this data. We use
          this data as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Plaid Account</h2>
        <p>
          A Plaid Account allows you to save your financial account connections
          and personal information in an account directly with Plaid to unlock
          faster onboarding and other services from Plaid and your chosen apps.
          We collect identifiers, financial data, commercial data, location
          data, and electronic network activity data. We use this data as
          described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Plaid Portal</h2>
        <p>
          Plaid Portal is a privacy tool that you can use as a way to control
          connections to apps that you have made through Plaid. You can use
          Plaid Portal to view connections to apps in a single dashboard,
          disconnect financial accounts from apps, or delete data from Plaid's
          systems. You can also use Plaid Portal to view and manage your Plaid
          Account. We collect identifiers, financial data, commercial data,
          location data, and electronic network activity data. We use this data
          as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Protect</h2>
        <p>
          Protect is a real time fraud intelligence system that helps Plaid and
          the apps and services you use detect and reduce the risk of fraud. We
          collect identifiers, financial data, commercial data, location data,
          electronic network activity data, and we derive inferences from this
          data. We use this data as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Signal</h2>
        <p>
          Signal helps the apps and services you use provide a better user
          experience to you, like providing you with faster access to your funds
          or reducing the risk of fraud or a payment being returned or not being
          completed. We collect identifiers, financial data, commercial data,
          location data, electronic network activity data, and we derive
          inferences from this data. We use this data as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Statements</h2>
        <p>
          Statements allows you to share copies of your financial account
          statements with Plaid and the apps and services you choose. We may
          collect identifiers, financial data, commercial data, location data,
          and electronic network activity data. We use this data as described in
          the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Transactions</h2>
        <p>
          Transactions and its related products enable you to provide access to
          transaction histories from your financial accounts to Plaid and apps
          and services you choose so they can provide you with things like
          personal finance management tools, expense reporting, cash flow
          modeling, and more. We may collect identifiers, financial data,
          commercial data, location data, electronic network activity data, and
          we derive inferences and financial insights from this data. We use
          this data as specified above in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>
        <h2>Transfer</h2>
        <p>
          Transfer enables you to authorize a developer to send payments to you,
          or collect payments from you, through bank transfers, on either a one
          time or recurring basis. This includes verifying you own the account
          to or from which the payment will be sent, assessing the risk that the
          payment may not be completed or is fraudulent, electronically
          processing payment instructions, and providing confirmation to the
          developer that the payment was completed. We may collect identifiers,
          financial data, location data, and electronic network activity data.
          We use this data as described in the{' '}
          <Link href='#how-we-use-your-data'>
            <a>How We Use Your Data</a>
          </Link>{' '}
          section of this Policy.
        </p>

        <hr className='section-divider' />
        <h2>Consumer Privacy Notice</h2>
        <p>
          <span>Last Updated: </span>
          <span>February 22, 2024</span>
        </p>

        <table className='eupp-table'>
          <thead>
            <tr>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span></span>
                </p>
              </td>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <strong>
                    WHAT DOES PLAID DO WITH YOUR PERSONAL INFORMATION?
                  </strong>
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>Why?</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    Financial companies choose how they share your personal
                    information. Federal law gives consumers the right to limit
                    some but not all sharing. Federal law also requires us to
                    tell you how we collect, share, and protect your personal
                    information. Please read this notice carefully to understand
                    what we do.
                  </span>
                </p>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>What?</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    The types of personal information we collect and share
                    depend on the product or service you, or the app developer
                    you are connecting to, use. This information can include:
                  </span>
                </p>
                <ul>
                  <li>
                    <span>Social Security number and account balances</span>
                  </li>
                  <li>
                    <span>Account transactions and transaction history</span>
                  </li>
                  <li>
                    <span>Income and employment information</span>
                  </li>
                </ul>
                <p>
                  <span>
                    For more information on the types of personal information we
                    collect, please see the{' '}
                    <Link href='#data-we-collect-and-categories-of-sources'>
                      <a>Data We Collect or Derive</a>
                    </Link>{' '}
                    section of our End User Privacy Policy.
                  </span>
                </p>
                <p>
                  <span>
                    When you are no longer our consumer, we continue to use and
                    share your information as described in this notice.
                  </span>
                </p>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>How?</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    All financial companies need to share consumers’ personal
                    information to provide their products and services. In the
                    section below, we list the reasons financial companies can
                    share their consumers’ personal information; the reasons
                    Plaid chooses to share; and whether you can limit this
                    sharing.
                  </span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>

        <table className='eupp-table'>
          <thead>
            <tr>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <strong>
                    Reasons we can share your personal information{' '}
                  </strong>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <strong>Does Plaid share?</strong>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <strong>Can you limit this sharing?</strong>
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>For our everyday business purposes –</strong>
                  <br />
                  <span>
                    such as to connect and share data from your financial
                    accounts to power the apps you use, as well as to protect
                    against fraud or misuse, respond to court orders and legal
                    investigations, or report to credit bureaus.
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>Yes</span>
                </p>
              </td>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>No</span>
                </p>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>For our marketing purposes </strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>No</span>
                </p>
              </td>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>We don’t share</span>
                </p>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>
                    For joint marketing with other financial companies
                  </strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>No</span>
                </p>
              </td>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>We don’t share</span>
                </p>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>
                    For our affiliates' everyday business purposes - information
                    about transactions and experiences
                  </strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>Yes</span>
                </p>
              </td>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>Yes, and we only share at your direction </span>
                </p>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>
                    For our affiliates' everyday business purposes - information
                    about creditworthiness
                  </strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>Yes</span>
                </p>
              </td>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>Yes, and we only share at your direction</span>
                </p>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>For nonaffiliates to market to you</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>No</span>
                </p>
              </td>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>We don’t share</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>

        <table className='eupp-table'>
          <tbody>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>Questions</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    Contact us{' '}
                    <Link href='mailto:privacy@plaid.com'>
                      <a>privacy@plaid.com</a>
                    </Link>{' '}
                  </span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>

        <table className='eupp-table'>
          <thead>
            <tr>
              <th scope='col' colSpan='2' rowSpan='1'>
                <p>
                  <strong>Who we are</strong>
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>Who is providing this notice?</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>Plaid Inc.</span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>

        <table className='eupp-table'>
          <thead>
            <tr>
              <th scope='col' colSpan='2' rowSpan='1'>
                <p>
                  <strong>What we do</strong>
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>
                    How does Plaid protect my personal information?
                  </strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    To protect your personal information from unauthorized
                    access and use, we use security measures that comply with
                    federal law. These measures include computer safeguards and
                    secured files and buildings.
                  </span>
                </p>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>
                    How does Plaid collect my personal information?
                  </strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    As explained in more detail in the{' '}
                    <Link href='#data-we-collect-and-categories-of-sources'>
                      <a>Data We Collect or Derive</a>
                    </Link>{' '}
                    section of our End User Privacy Policy, we collect personal
                    information you provide to us and collect your personal
                    information from other companies when you use our products
                    or services. This includes, for example, when you use Plaid
                    Portal or when you use Plaid to connect and share your data
                    to power the applications (“apps”) you use.
                  </span>
                </p>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>Why can’t I limit all sharing?</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>Federal law gives you the right to limit only:</span>
                </p>
                <ul>
                  <li>
                    <span>
                      sharing for affiliates’ everyday business purposes –
                      information about your creditworthiness;
                    </span>
                  </li>
                  <li>
                    <span>
                      affiliates from using your information to market to you;
                    </span>
                  </li>
                  <li>
                    <span>sharing for nonaffiliates to market to you.</span>
                  </li>
                </ul>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    State laws and individual companies may give you additional
                    rights to limit sharing. See below for more on your rights
                    under state law.
                  </span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>

        <table className='eupp-table'>
          <thead>
            <tr>
              <th scope='col' colSpan='2' rowSpan='1'>
                <p>
                  <strong>Definitions</strong>
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>Affiliates</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    Companies related by common ownership or control. They can
                    be financial and nonfinancial companies.
                  </span>
                </p>
                <ul>
                  <li>
                    <span>
                      Our affiliates include Plaid Financial Ltd., Plaid, B.V.,
                      and Plaid Consumer Reporting Agency Inc. (“Plaid CRA”).
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>Nonaffiliates</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    Companies not related by common ownership or control. They
                    can be financial and nonfinancial companies.
                  </span>
                </p>
                <ul>
                  <li>
                    <span>
                      Plaid does not share with nonaffiliates so they can market
                      to you.
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <strong>Joint marketing</strong>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    A formal agreement between nonaffiliated financial companies
                    that together market financial products or services to you.
                  </span>
                </p>
                <ul>
                  <li>
                    <span>Plaid does not joint market.</span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <td colSpan='2' rowSpan='1'>
                <p>
                  <strong>Other Important Information </strong>
                </p>
              </td>
            </tr>
            <tr>
              <td colSpan='2' rowSpan='1'>
                <p>
                  <strong>California: </strong>
                  <span>
                    If you are a resident of California, we will not share
                    personal information we collect about you except to the
                    extent permitted under California law.
                  </span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <strong>Vermont: </strong>
                  <span>
                    If you are a resident of Vermont, we will not share personal
                    information we collect about you with non-affiliates unless
                    the law allows or you provide authorization.
                  </span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </main>
    </IosTemplate>
  );
};

export default LegalIos;
